import { getCurrentEnv } from '@/api/request';
import { useAppStore } from '@/store/app';
import nativeApi from '@bigo/nativeapi-bigo';

import * as Sentry from '@sentry/vue';

// 导出马甲包主题相关工具
export { getPartyHeroImage, initTheme } from './theme';

/**
 * 获取token错误上报sentry
 * @param {*} err
 */
export function catchSentryTokenError(err: any) {
  let errorText = '';
  try {
    errorText = JSON.stringify(err);
  } catch (error) {
    //
  }
  Sentry.captureException(new Error(errorText), {
    extra: {
      category: 'getToken',
      error: err?.message,
      extras: {
        getTokenError: window.location.href,
      },
    },
  });
}

export const isBigolive = navigator.userAgent.match(/(bigo-baiguoyuan).*(_bigolive_)/i);

console.log('isBigoliveinit', navigator.userAgent, isBigolive);

/**
 * 是否ios端
 */
export const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent);

/**
 * 是否是安卓
 */
export const isAndroid = /(android)/i.test(navigator.userAgent);

/**
 * 检测是否能使用localStorage
 * @returns {boolean}
 */
function judgeIsCanUseLocalStorage() {
  let isCanUseLocalStorage = true;
  try {
    window.localStorage.setItem('testStorage', 'testStorage');
  } catch (err) {
    isCanUseLocalStorage = false;
  }
  return isCanUseLocalStorage;
}

/**
 * 是否可以用localstorage
 * @type {boolean}
 */
export const isCanUseLocalStorage = judgeIsCanUseLocalStorage();

/**
 * （兼容safari无痕浏览）localStorage.setItem
 * @param key
 * @param value
 */
export function storageSetItem(key: string, value: string): void {
  if (isCanUseLocalStorage) {
    window.localStorage.setItem(key, value);
  }
}

/**
 * （兼容safari无痕浏览）localStorage.getItem
 * @param key
 * @returns {string | null}
 */
export function storageGetItem(key: string): string | null {
  if (isCanUseLocalStorage) {
    return window.localStorage.getItem(key);
  }
  return '';
}

interface UrlInfoMap {
  [key: string]: string;
}
export const getParamFromUrl = (param: string): string => {
  const urlInfoMap: UrlInfoMap = window.document.location.search
    .substring(1)
    .split('&')
    .reduce((acc: UrlInfoMap, cur: string) => {
      const [key, value] = cur.split('=');
      acc[key] = value;
      return acc;
    }, {});

  return urlInfoMap[param] || '';
};

const ABSOLUTE_URL_RE = /^[a-z][a-z\d+\-.]*:\/\//i;

export const setUrlQueryParam = (url = '', key = '', value = '') => {
  if (!url || !key) return url;

  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set(key, value);

    if (ABSOLUTE_URL_RE.test(url)) {
      return urlObj.toString();
    }

    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  } catch {
    const [urlWithoutHash, hash = ''] = url.split('#');
    const separator = urlWithoutHash.includes('?') ? '&' : '?';
    const hashPart = hash ? `#${hash}` : '';
    return `${urlWithoutHash}${separator}${encodeURIComponent(key)}=${encodeURIComponent(
      value,
    )}${hashPart}`;
  }
};

// 根据testUid获取token
export const getTokenByTestUid = async () => {
  const BIGOLIVETESTTOKEN = 'bigolive_test_token';
  const BIGOLIVETESTUID = 'bigolive_test_uid';
  const testUid = getParamFromUrl('testUid') || storageGetItem(BIGOLIVETESTUID);
  if (testUid) {
    storageSetItem(BIGOLIVETESTUID, testUid);
    const { time, token, uid } = JSON.parse(storageGetItem(BIGOLIVETESTTOKEN) || '{}');
    if (time && time - Date.now() > 90 * 1000 && testUid === uid && token) {
      return token;
    }
    const response = await fetch('https://livelbs-test-pro.bigo.sg/login/token/get-auth-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      body: JSON.stringify({ appid: 60, uid: testUid }),
    });

    const { rescode, token: TestToken, duration }: any = await response.json();

    if (rescode === 0) {
      storageSetItem(
        BIGOLIVETESTTOKEN,
        JSON.stringify({ time: Date.now() + duration * 1000, token: TestToken, uid: testUid }),
      );
      return TestToken;
    }
  }
};

export const gotoLoginPage = () => {
  if (!isBigolive) {
    return;
  }
  nativeApi.registerMethod('getLoginStatus', 'getLoginStatus');
  nativeApi.registerMethod('visitorLogin', 'visitorLogin');
  nativeApi
    .getLoginStatus()
    .then((e: any) => {
      if (!e.loginStatus) {
        nativeApi.visitorLogin().then((e) => {
          if (e.loginResult === 1) {
            window.location.reload();
          }
        });
      }
    })
    .catch((e: any) => {
      console.log(e, '获取登录状态失败');
    });
};

export const getTokenFromNativiApi = (): Promise<any> =>
  new Promise((resolve, reject) => {
    nativeApi
      .getToken()
      .then((res: any) => {
        const { token, code } = res;
        if (token) {
          resolve(token);
        } else {
          reject(new Error(`getToken error code ${code}`));
        }
      })
      .catch((err: any) => {
        reject(err);
        catchSentryTokenError(err);
      });
  });

export const getToken = (): Promise<any> =>
  new Promise((resolve, reject) => {
    // 不在端内就去store取token
    if (!isBigolive) {
      const storeToken = useAppStore().token;
      if (storeToken) {
        resolve(storeToken);
      } else {
        reject();
      }
    }

    nativeApi
      .getToken()
      .then((res: any) => {
        const { token, code } = res;
        if (token) {
          resolve(token);
        } else {
          reject(new Error(`getToken error code ${code}`));
        }
      })
      .catch((err: any) => {
        reject(err);
        catchSentryTokenError(err);
      });
  });

/**
 * 生成请求id（9位数字），后端查询日志用
 */
export function generateSeqId() {
  return Math.floor(Math.random() * (9e8 - 1)) + 1e8;
}

/**
 * deeplink 跳转到web页
 * @param {*} url 链接地址
 * @param {*} openMode 0打开内部页面，1打开外部页面
 */
export const goWeb = async (url: string, openMode = 0): Promise<void> => {
  if (!/^https:\/\//.test(url)) {
    if (process.env.VUE_APP_CURRENTMODE !== 'prod') {
      console.warn('goWeb url must start with https://');
    }
    return;
  }
  goDeeplink(`//web?url=${encodeURIComponent(url)}&openMode=${openMode}`);
};

interface UaInfo {
  phoneName: string;
  appName: string;
  appVersion: string;
  sysName: string;
  sysVersion: string;
  appEnv: string;
  deviceid: string;
  userid: string;
  appVersionCode: string;
  countryCode: string;
  [key: string]: string | undefined;
}

export function getUaInfo(): UaInfo {
  try {
    const ua = navigator.userAgent;
    const match = ua.match(/BIGO-baiguoyuan \((.+)\)/i);

    if (match) {
      const info = match[1];
      const keys: string[] = [
        'phoneName',
        'appName',
        'appVersion',
        'sysName',
        'sysVersion',
        'appEnv',
        'deviceid',
        'userid',
        'appVersionCode',
        'countryCode',
      ];

      const obj = {} as UaInfo;
      const valueArr: string[] = info.split('__');
      keys.forEach((name: string, index: number) => {
        obj[name] = valueArr[index];
      });
      return obj;
    }

    return {} as UaInfo;
  } catch (error) {
    return {} as UaInfo;
  }
}
// m.piojm.tech和回源到agent-oss-src.bigoapp.tv的CDN域名可以使用同域登录方案
// 目前经纪后台的cdn域名都是回源到agent-oss-src.bigoapp.tv，直接判断是否是cdn域名即可
export function isUseSameDomain() {
  const fullDomain = window.location.hostname;
  return (
    fullDomain.endsWith('.cloudfront.net') ||
    ['m.piojm.tech', 'agent-oss-src.bigoapp.tv'].includes(fullDomain)
  );
}

let sdkChannelId = 0;
const BIGO_CHANNEL = 60;

export const getSdkChannelId = (): Promise<any> => {
  if (sdkChannelId) return Promise.resolve(sdkChannelId);
  return new Promise((resolve) => {
    const resolveNew = (val) => {
      if (!val && !sdkChannelId) {
        setTimeout(() => resolveNew(BIGO_CHANNEL), 1000);
        return;
      }
      if (val) {
        sdkChannelId = val;
      }
      resolve(+sdkChannelId);
    };

    // debug
    if (getParamFromUrl('sdkChannel')) {
      resolveNew(getParamFromUrl('sdkChannel'));
      return;
    }

    // sdk只有安卓
    if (!window.live || !window.live.getSdkChannelId) {
      // bigolive 未注入对象
      resolveNew(BIGO_CHANNEL);
      return;
    }

    nativeApi.getSdkChannelId().then(resolveNew);
  });
};

export function goDeeplink(link: string): void {
  let protocol = '';

  if (sdkChannelId) {
    protocol = sdkChannelId === BIGO_CHANNEL ? 'bigolive' : `bigolivesdk${sdkChannelId}`;
    console.log('protocol:link===>>>>', `${protocol}:${link}`);
    window.location.href = `${protocol}:${link}`;
    return;
  }

  getSdkChannelId().then((channelId) => {
    protocol = channelId === BIGO_CHANNEL ? 'bigolive' : `bigolivesdk${channelId}`;
    console.log('protocol:link===>>>>', `${protocol}:${link}`);
    window.location.href = `${protocol}:${link}`;
  });
}
// 端内：跳转到主播房间
export function goAnchorRoom(uid: string, roomid: string): void {
  /**
   * likee 进入普通直播房间
   * likevideo://livevideoshow?roomid=xxx&uid=xxx
   */
  if (+uid !== 0 && +uid !== -1 && +roomid !== 0 && +roomid !== -1) {
    // 不能跳转当前直播间
    if (getParamFromUrl('roomId') === roomid) return;
    goDeeplink(`//livevideoshow?roomid=${roomid}&uid=${uid}&fromWeb=1`);
  }
}
// 端外：跳转到主播直播间
type envType = 'mock' | 'dev' | 'test' | 'gray' | 'prod';
export function goAnchorRoomOut(bigoid: string): void {
  const env = getCurrentEnv() as envType;
  const urlMap: Record<string, string> = {
    mock: 'https://ta-www.bigo.tv', // mock 环境使用 test 地址
    dev: 'https://ta-www.bigo.tv', // dev 环境使用 test 地址
    test: 'https://ta-www.bigo.tv',
    gray: 'https://bggray-www.bigo.tv',
    prod: 'https://www.bigo.tv',
  };
  // 使用默认值,确保类型安全
  const baseUrl = urlMap[env] || urlMap.prod;
  window.open(`${baseUrl}/${bigoid}`, '_blank');
}

export function doBlockHostReplace(url: string) {
  return window.doBlockHostReplace ? window.doBlockHostReplace(url).url : url;
}

export const INSTALL_PACKAGE_INFO = window.INSTALL_PACKAGE_INFO || {};

export const isPartyHero = () => {
  const pkg = window?.INSTALL_PACKAGE_INFO?.install_package || getParamFromUrl('install_package');
  if (!pkg) return false;
  return ['2', '4'].includes(pkg);
};
